import toast, { Toast } from 'react-hot-toast'

import { ubemIoApi } from '../../services/ubem'
import { setError } from '../error/reducers'
import { AsyncTaskConfig } from './consts'
import { asyncSuccess, clearInitiator } from './reducers'
import {
  AsyncTaskStatusResponse,
  CheckAsyncTaskStatusArg,
  StartAsyncTaskPayload,
  StartAsyncTaskResponse,
} from './types'

const toastOptions = (
  endpoint: keyof typeof AsyncTaskConfig,
  additional?: Partial<Pick<Toast, 'id' | 'style' | 'className'>>,
): Partial<Toast> => ({
  id: endpoint,
  position: 'bottom-center',
  ...additional,
  style: { minWidth: '30%' },
})

export const asyncApi = ubemIoApi.injectEndpoints({
  endpoints: (builder) => ({
    startAsyncTask: builder.mutation<StartAsyncTaskResponse, StartAsyncTaskPayload>({
      query: ({ endpoint, body, method }) => ({
        url: `api/${AsyncTaskConfig[endpoint].route}`,
        body,
        method: method ?? 'POST',
      }),
      onQueryStarted: async ({ endpoint }, { dispatch, queryFulfilled }) => {
        try {
          dispatch(clearInitiator())
          if (AsyncTaskConfig[endpoint].usesToasts)
            toast.loading('Scheduling async task...', toastOptions(endpoint))
          const { data } = await queryFulfilled
          if (AsyncTaskConfig[endpoint].usesToasts)
            toast.loading(data.response?.status ?? '', toastOptions(endpoint))
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: AsyncTaskConfig[endpoint].slice,
              key: endpoint,
            }),
          )
          if (AsyncTaskConfig[endpoint].usesToasts) toast.dismiss(endpoint)
        }
      },
      transformResponse: (response: { status: string }, meta, arg) => {
        return {
          response,
          taskLocation: meta?.response?.headers.get('location'),
        }
      },
    }),
    checkAsyncStatus: builder.query<AsyncTaskStatusResponse, CheckAsyncTaskStatusArg>({
      query: ({ url }) => ({
        url,
        method: 'GET',
      }),
      onQueryStarted: async ({ endpoint }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          if (data.result) {
            dispatch(asyncSuccess({ endpoint, result: data.result }))
            if (AsyncTaskConfig[endpoint].usesToasts)
              toast.success('Success!', toastOptions(endpoint))
          } else {
            if (AsyncTaskConfig[endpoint].usesToasts)
              toast.loading(data.message.replaceAll('_', ' '), toastOptions(endpoint))
          }
          const tagsToInvalidate = AsyncTaskConfig[endpoint].invalidateTags
          if (tagsToInvalidate) {
            dispatch(ubemIoApi.util.invalidateTags(tagsToInvalidate))
          }
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow: AsyncTaskConfig[endpoint].slice,
              key: endpoint,
            }),
          )
          if (AsyncTaskConfig[endpoint].usesToasts) toast.dismiss(endpoint)
        }
      },
    }),
  }),
})

export const { startAsyncTask, checkAsyncStatus } = asyncApi.endpoints
